// Server Control page — the small pure helpers (words, colours, sizes, times), kept apart so they are easy to test.
import { nextStage, stageName, stageParts, STAGE_ORDER } from "../Policy/OnlinePolicy/utils/relianceStages";

export const TONE = {
  online: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6", label: "Online" },
  stopped: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA", label: "Stopped" },
  errored: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA", label: "Errored" },
  starting: { color: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF", label: "Starting" },
  stopping: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89", label: "Stopping" },
  unknown: { color: "#475467", bg: "#F2F4F7", border: "#EAECF0", label: "Unknown" },
  unreachable: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89", label: "Not reachable" },
};

/** The server's state from the overview: online / stopped / errored / starting / stopping / unreachable / unknown. */
export const serverState = (agent) => {
  if (!agent || !agent.reachable) return "unreachable";
  if (!agent.found || !agent.process) return "unknown";
  const s = String(agent.process.status || "").toLowerCase();
  if (s === "online") return "online";
  if (s === "stopped") return "stopped";
  if (s === "errored") return "errored";
  if (s === "launching" || s === "waiting restart") return "starting";
  if (s === "stopping") return "stopping";
  return "unknown";
};

/** Which buttons make sense now. */
export const actionsFor = (state) => ({
  start: ["stopped", "errored", "unknown"].includes(state),
  restart: ["online", "errored"].includes(state),
  stop: ["online", "starting", "errored"].includes(state),
});

export const formatBytes = (n) => {
  const v = Number(n) || 0;
  if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(1)} GB`;
  if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1)} MB`;
  if (v >= 1024) return `${Math.round(v / 1024)} KB`;
  return `${v} B`;
};

/** 3d 4h · 4h 12m · 12m · 40s */
export const formatDuration = (ms) => {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${s}s`;
};

/** "2m ago" from a date, against now. */
export const ago = (date, now = Date.now()) => {
  if (!date) return "—";
  const t = new Date(date).getTime();
  if (Number.isNaN(t)) return "—";
  return `${formatDuration(now - t)} ago`;
};

/** A heartbeat older than this means the window may be stuck. */
export const STALE_HEARTBEAT_MS = 2 * 60 * 1000;
export const isStale = (date, now = Date.now()) => !!date && now - new Date(date).getTime() > STALE_HEARTBEAT_MS;

/** "KYC (step 2 of 3)" for a running Reliance window, else null. */
export const windowStep = (w) => {
  const part = stageParts(w && w.relianceStageView)[0];
  if (!part) return null;
  const step = nextStage(part.stages);
  if (!step) return "Finishing (all steps done)";
  return `${stageName(step)} (step ${STAGE_ORDER.indexOf(step) + 1} of ${STAGE_ORDER.length})`;
};

/** Percent used, 0..100. */
export const usedPercent = (total, free) => {
  const t = Number(total) || 0;
  if (!t) return 0;
  return Math.max(0, Math.min(100, Math.round(((t - (Number(free) || 0)) / t) * 100)));
};

/** What each server action does — the confirm dialog's words. */
export const actionText = (action, running = 0) => {
  if (action === "start") return { title: "Start the automation server?", lines: ["pm2 process 3 (server) is started.", "Queued policies start being worked on again."], button: "Start" };
  const what = action === "stop" ? "stopped" : "restarted";
  const lines = running
    ? [
        `${running} window(s) are running now. Each stops at its next safe step, closes and goes on hold (a payment already being made finishes first).`,
        `When none is running any more, pm2 process 3 (server) is ${what}.`,
        "Held policies continue from their step when you Release them.",
      ]
    : [`Nothing is running now, so pm2 process 3 (server) is ${what} at once.`];
  if (action === "stop") lines.push("While it is stopped no policy is worked on. Only this one server is touched - no other app.");
  return { title: action === "stop" ? "Stop the automation server?" : "Restart the automation server?", lines, button: action === "stop" ? "Stop server" : "Restart server" };
};
